import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useItem, useItemAssets, useItemUploads, deleteItemUpload, queueFanoutJob,
} from '../data/firestore';
import { downloadAsset } from '../data/uploadService';
import Uploader from '../components/Uploader';
import DropLinkManager from '../components/DropLinkManager';
import Collapsible from '../components/Collapsible';

// One item's workspace: owner uploads, guest drop links, the asset list (with
// live meta-worker status) and a demo fan-out job trigger. This page exists to
// exercise every foundation pattern end to end — reshape it for your domain.

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

const metaBadge = (a) => {
  const st = a.meta?.status || 'none';
  const color = st === 'done' ? '#7ce38b' : st === 'error' ? 'var(--accent, #ff6b6b)' : st === 'none' ? 'var(--muted)' : '#ffd166';
  const label = st === 'done' ? 'verified' : st === 'running' ? 'verifying…' : st;
  return <span style={{ fontSize: 11, color }} title={a.meta?.message || ''}>{label}</span>;
};

export default function ItemDetail() {
  const { id } = useParams();
  const { doc: item } = useItem(id);
  const { docs: assets } = useItemAssets(id);
  const { docs: uploads } = useItemUploads(id);
  const [queuedJob, setQueuedJob] = useState('');

  if (item === undefined) return <div className="cs-page"><p className="cs-help">Loading…</p></div>;
  if (item === null) {
    return (
      <div className="cs-page">
        <p className="cs-help">Item not found. <Link to="/">Back to items</Link></p>
      </div>
    );
  }

  // Live in-flight guest uploads (mirrored by the backend from drop heartbeats).
  const liveUploads = (uploads || []).filter(
    (u) => u.status === 'uploading' && Date.now() - (u.updatedMs || 0) < 5 * 60 * 1000
  );

  return (
    <div className="cs-page">
      <p style={{ margin: 0 }}><Link to="/" className="cs-help">← Items</Link></p>
      <h1 style={{ marginTop: 6 }}>{item.name}</h1>
      {item.description && <p className="cs-help">{item.description}</p>}

      <Collapsible title="Upload files" defaultOpen>
        <Uploader item={item} />
      </Collapsible>

      <Collapsible title="Guest upload links">
        <DropLinkManager item={item} />
      </Collapsible>

      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Assets</h2>
          <span className="cs-help">{(assets || []).length} recorded</span>
          <span style={{ flex: 1 }} />
          <button
            className="cs-btn cs-btn-outline cs-btn-sm"
            title="Demo fan-out job: hashes every object under this item's S3 prefix on parallel Fargate runners and writes a manifest.json (watch it on the Jobs page)"
            onClick={async () => {
              const jobId = await queueFanoutJob('manifest', { prefix: item.s3Prefix }, { title: `Manifest · ${item.name}` });
              setQueuedJob(jobId);
            }}
          >
            ⚙ Build S3 manifest (fan-out demo)
          </button>
        </div>
        {queuedJob && (
          <p className="cs-help" style={{ marginTop: 6 }}>
            Queued <code>{queuedJob}</code> — follow it on the <Link to="/jobs">Jobs</Link> page.
          </p>
        )}

        {liveUploads.map((u) => (
          <div key={u.id} className="cs-card" style={{ padding: 10, marginTop: 10, opacity: 0.85 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
              <span>⬆ {u.name}</span>
              <span className="cs-help" style={{ fontSize: 12 }}>
                {u.contributor ? `${u.contributor} · ` : ''}uploading now · {u.pct || 0}%
              </span>
              <span style={{ flex: 1 }} />
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => deleteItemUpload(id, u.id)}>Dismiss</button>
            </div>
            <div className="cs-progress" style={{ height: 5, marginTop: 6 }}>
              <div style={{ width: `${u.pct || 0}%` }} />
            </div>
          </div>
        ))}

        {assets && !assets.length && !liveUploads.length && (
          <div className="cs-empty" style={{ marginTop: 12 }}>No assets yet — upload something above.</div>
        )}

        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {(assets || []).map((a) => (
            <div key={a.id} className="cs-card" style={{ padding: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>{a.kind === 'photo' ? '🖼' : a.kind === 'folder' ? '📁' : '📄'}</span>
                <strong style={{ fontSize: 13 }}>{a.name}</strong>
                {metaBadge(a)}
                <span className="cs-help" style={{ fontSize: 12 }}>
                  {fmtBytes(a.sizeBytes)}
                  {Array.isArray(a.parts) && a.parts.length > 1 ? ` · ${a.parts.length} files` : ''}
                  {a.contributor ? ` · from ${a.contributor}` : ''}
                </span>
                <span style={{ flex: 1 }} />
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  onClick={() => downloadAsset({ key: a.s3Key, name: a.name })}
                  title="Presigned download from the private raw bucket"
                >
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
